import i18n from 'i18next'
import { useMemo, createElement, type ReactNode } from 'react'
import { useNavigate } from 'react-router'
import {
    IconPlus,
    IconMoon,
    IconSun,
    IconFlask,
    IconUsers,
    IconListTree,
    IconSparkles,
    IconTable,
    IconUpload,
} from '@tabler/icons-react'
import useDarkMode from '@/utils/hooks/useDarkMode'
import type { CommandAction } from '@/components/editor/CommandPalette'
import { paths } from '@/lib/addresses'

/**
 * The Lilia menu's Actions — what ⌘K could always do, now the last group of
 * the menu's results while typing (navigation handoff §2). The tools
 * themselves, and your recent items, are the menu's own groups, so the old
 * "Navigate" and "Recent documents" entries that duplicated them are gone;
 * the places that aren't tools (Teams, Playgrounds) stay here.
 */

const ic = (icon: ReactNode) => icon

interface GlobalCommandActionsOptions {
    /** Opens the New-document chooser. Defaults to asking the app for it. */
    onNewDocument?: () => void
}

/** Opens the New-document chooser; `{ view: 'template' }` opens it on the templates (first run 5a). */
export const requestNewDocument = (detail?: { view?: 'template' }) =>
    window.dispatchEvent(new CustomEvent('lilia:new-document', { detail }))

export function useGlobalCommandActions(options: GlobalCommandActionsOptions = {}): CommandAction[] {
    const { onNewDocument = requestNewDocument } = options
    const navigate = useNavigate()
    const [isDark, setMode] = useDarkMode()

    return useMemo(() => [
        // Opens the chooser dialog (blank-with-settings · wizard · template),
        // not a direct create-and-navigate.
        { id: 'cmd-new', label: i18n.t('shell:actions.newDocument'), hint: i18n.t('shell:actions.newDocumentHint'), group: 'Actions', scope: 'command', icon: ic(createElement(IconPlus, { size: 15 })), run: onNewDocument },
        { id: 'cmd-new-table', label: i18n.t('shell:actions.newTable'), hint: i18n.t('shell:actions.newTableHint'), group: 'Actions', scope: 'command', icon: ic(createElement(IconTable, { size: 15 })), run: () => navigate(`${paths.tables}?new=1`) },
        { id: 'cmd-import-file', label: i18n.t('shell:actions.importFile'), hint: i18n.t('shell:actions.importFileHint'), group: 'Actions', scope: 'command', icon: ic(createElement(IconUpload, { size: 15 })), run: () => navigate(`${paths.import}?tab=import`) },
        { id: 'cmd-new-structure', label: i18n.t('shell:actions.newFromStructure'), hint: i18n.t('shell:actions.newFromStructureHint'), group: 'Actions', scope: 'command', icon: ic(createElement(IconListTree, { size: 15 })), run: () => navigate('/new-from-structure') },
        { id: 'cmd-ai-architect', label: i18n.t('shell:actions.askAi'), hint: i18n.t('shell:actions.askAiHint'), group: 'Actions', scope: 'command', icon: ic(createElement(IconSparkles, { size: 15 })), run: () => navigate('/ai') },
        { id: 'nav-teams', label: i18n.t('shell:actions.teams'), group: 'Actions', icon: ic(createElement(IconUsers, { size: 15 })), run: () => navigate('/teams') },
        { id: 'nav-playgrounds', label: i18n.t('shell:actions.playgrounds'), group: 'Actions', icon: ic(createElement(IconFlask, { size: 15 })), run: () => navigate('/playgrounds') },
        {
            id: 'cmd-theme',
            label: isDark ? i18n.t('shell:actions.toLightTheme') : i18n.t('shell:actions.toDarkTheme'),
            group: 'Actions',
            scope: 'command',
            icon: ic(createElement(isDark ? IconSun : IconMoon, { size: 15 })),
            run: () => setMode(isDark ? 'light' : 'dark'),
        },
    ] satisfies CommandAction[], [navigate, isDark, setMode, onNewDocument])
}
